import dayjs from "dayjs"
import { dayJstoDateOnly } from "../../../../utils/dayjs-to-date-only"
import { CalendarGrid } from "./grid"
import { CalendarTable } from "./table"
import {
  CalendarAppointment,
  eAppointmentCountingMethod,
} from "../../../../@client/Theasoft_ApiServer_Calendar"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"

export interface RenderCalendarProps {
  startDate: Date
  endDate: Date
  eventsByDate: Map<string, CalendarAppointment[]>
  statusByDate: Map<string, CalendarAppointment[]>
  holidaysByDate: Set<string>
  schoolHolidaysByDate: Set<string>
}

interface Props {
  startDate: Date
  endDate: Date
  appointmentCountMethod?: eAppointmentCountingMethod
  tableView?: boolean
}

export function Calendar({
  endDate,
  startDate,
  tableView,
  appointmentCountMethod = "NoCount",
}: Props) {
  const start = dayjs(startDate).startOf("month")
  const end = dayjs(endDate).endOf("month")

  const { data: holidays } = useSuspenseQueryGet(
    "/api/Holiday/GetHolidaysForDateRange",
    {
      endDate: dayJstoDateOnly(end),
      startDate: dayJstoDateOnly(start),
    }
  )

  const { data: schoolHolidays } = useSuspenseQueryGet(
    "/api/SchoolHoliday/GetSchoolHolidaysForDateRange",
    {
      endDate: dayJstoDateOnly(end),
      startDate: dayJstoDateOnly(start),
    }
  )

  const { data: allEvents } = useSuspenseQueryGet(
    "/api/Calendar/GetMyEventsForDateRange",
    {
      endDate: dayJstoDateOnly(end),
      startDate: dayJstoDateOnly(start),
      method: appointmentCountMethod,
      withPossibleCasts: true,
      onlyReleasedDuties: false,
    }
  )

  const { data: allStatus } = useSuspenseQueryGet(
    "/api/Calendar/GetMyStatusForDateRange",
    {
      endDate: dayJstoDateOnly(end),
      startDate: dayJstoDateOnly(start),
    }
  )

  const eventsByDate = new Map(
    allEvents.map(([date, events]) => [
      dayjs(date).format("YYYY-MM-DD"),
      events,
    ])
  )

  const statusByDate = new Map(
    allStatus.map(([date, events]) => [
      dayjs(date).format("YYYY-MM-DD"),
      events,
    ])
  )

  const holidaysByDate = new Set(
    holidays.map((holiday) => dayjs(holiday.date).format("YYYY-MM-DD"))
  )

  const schoolHolidaysByDate = schoolHolidays.reduce((set, holiday) => {
    const start = dayjs(holiday.startDate)
    const end = dayjs(holiday.endDate)

    for (
      let d = start;
      d.isBefore(end) || d.isSame(end, "day");
      d = d.add(1, "day")
    ) {
      set.add(d.format("YYYY-MM-DD"))
    }

    return set
  }, new Set<string>())

  return tableView ? (
    <CalendarTable
      endDate={end.toDate()}
      startDate={start.toDate()}
      eventsByDate={eventsByDate}
      holidaysByDate={holidaysByDate}
      schoolHolidaysByDate={schoolHolidaysByDate}
      statusByDate={statusByDate}
    />
  ) : (
    <CalendarGrid
      endDate={end.toDate()}
      startDate={start.toDate()}
      eventsByDate={eventsByDate}
      holidaysByDate={holidaysByDate}
      schoolHolidaysByDate={schoolHolidaysByDate}
      statusByDate={statusByDate}
    />
  )
}
